/**
 * ShiftBlockedDialog — the questions the server refuses to guess the answer to.
 *
 * Four of these come back from the batch endpoint, and one is raised locally:
 *
 *  - `double_booked` (local)   the officer is already on another job in that
 *                              window. Named with the job number and the
 *                              window, because "conflict" is not information.
 *  - `overtime_acknowledgement_required`   projected weekly hours across every
 *                              job. Hours only - never a pay or bill amount.
 *  - `qualification_acknowledgement_required`  an armed post and no current
 *                              licence. A warning with an acknowledgement,
 *                              never a block: that is a manager's call and it
 *                              is kept on the shift.
 *  - `call_off_reason_required` / `call_off_note_required`  somebody is being
 *                              taken off a shift. Reasons come from the
 *                              server's managed list; "Other" needs a note.
 *
 * Filling a row can raise the same question seven times, so the two
 * acknowledgements answer once per ACTION rather than once per cell. Overtime
 * folds to one dialog listing every officer who crosses the threshold with
 * their projected weekly hours, and a single confirmation is recorded against
 * every affected shift - the old behaviour showed one cell's hours behind an
 * "all 7" button, which asked the manager to acknowledge figures they could
 * not see. A single-cell assignment raises one item and keeps the single
 * dialog. A call-off reason is never answered in bulk: each one is a different
 * person not coming to work.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { AlertTriangle, Clock, ShieldAlert, UserMinus } from 'lucide-react';

const META = {
  double_booked: {
    icon: AlertTriangle,
    title: 'Already working elsewhere',
    confirm: 'Schedule anyway',
  },
  overtime_acknowledgement_required: {
    icon: Clock,
    title: 'This crosses 40 hours',
    confirm: 'Acknowledge and continue',
  },
  qualification_acknowledgement_required: {
    icon: ShieldAlert,
    title: 'No current licence for an armed post',
    confirm: 'Acknowledge and assign',
  },
  call_off_reason_required: {
    icon: UserMinus,
    title: 'Why is this officer coming off the shift?',
    confirm: 'Record and replace',
  },
  call_off_note_required: {
    icon: UserMinus,
    title: '"Other" needs a note',
    confirm: 'Record and replace',
  },
};

/** Not questions - refusals. The server applied nothing and no answer from
 *  here can change that, so offering a "Continue" button is a lie: it re-sends
 *  a change that will be refused again. These get the reason and a way out. */
const REFUSALS = {
  not_draft: 'That shift is no longer a draft, so it cannot be edited here.',
  not_in_this_week: 'That shift is not part of this week.',
  nothing_to_delete: 'There was no shift to remove.',
};

export function ShiftBlockedDialog({ item, remaining, reasons, names, overtimeGroup,
  onResolve, onResolveAll, onCancel }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setReason(item?.code === 'call_off_note_required' ? 'Other' : '');
    setNote('');
  }, [item?.key, item?.code]);

  if (!item) return null;

  const refusal = REFUSALS[item.code] || (!META[item.code] ? item.message : null);
  if (refusal) {
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
        <DialogContent className="max-w-md" data-testid={`blocked-dialog-${item.code}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
              <AlertTriangle className="h-4 w-4 text-warning" /> This change was not applied
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed"
              data-testid="blocked-dialog-message">
              {refusal} Everything else in that batch was saved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onCancel} data-testid="blocked-dialog-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const meta = META[item.code] || { icon: AlertTriangle, title: 'This needs a decision', confirm: 'Continue' };
  const Icon = meta.icon;
  const isCallOff = item.code === 'call_off_reason_required' || item.code === 'call_off_note_required';
  const needsNote = isCallOff && (reason || '').toLowerCase() === 'other';
  const ready = !isCallOff || (reason && (!needsNote || note.trim()));

  // One overtime question per action. A single-cell assignment raises exactly
  // one item, so it keeps the single dialog it always had.
  const otOfficers = overtimeGroup?.officers || [];
  const otShifts = overtimeGroup?.shiftCount || 0;
  const grouped = item.code === 'overtime_acknowledgement_required' && otShifts > 1;
  const bulkable = !isCallOff && !grouped && remaining > 0;
  const label = (id) => names?.get?.(id) || 'Officer';
  const threshold = otOfficers[0]?.threshold_hours ?? item.threshold_hours;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid={`blocked-dialog-${item.code}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base md:text-lg">
            <Icon className="h-4 w-4 text-warning" /> {meta.title}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed" data-testid="blocked-dialog-message">
            {grouped
              ? `${otOfficers.length === 1
                ? 'This officer goes'
                : `${otOfficers.length} officers go`} over ${threshold}h this week across all `
                + `jobs on this change, over ${otShifts} shift${otShifts === 1 ? '' : 's'}. `
                + 'Acknowledge once and it is recorded against every one of them.'
              : item.message}
          </DialogDescription>
        </DialogHeader>

        {grouped && (
          <div className="rounded-lg border border-line bg-surface-sunken divide-y divide-line-subtle
                          max-h-64 overflow-y-auto"
            data-testid="blocked-dialog-overtime-group">
            {otOfficers.map((o) => (
              <div key={o.employee_id} className="flex items-baseline justify-between gap-3 px-3 py-2"
                data-testid={`overtime-officer-${o.employee_id}`}>
                <span className="text-sm text-fg truncate">{label(o.employee_id)}</span>
                <span className="text-sm text-fg tabular-nums whitespace-nowrap">
                  {o.projected_week_hours}h
                  <span className="text-fg-muted"> · {o.hours_over}h over</span>
                </span>
              </div>
            ))}
          </div>
        )}

        {item.code === 'overtime_acknowledgement_required' && !grouped && (
          <div className="rounded-lg border border-line bg-surface-sunken p-3 text-sm"
            data-testid="blocked-dialog-overtime-detail">
            <div className="flex items-baseline justify-between">
              <span className="text-fg-muted">Projected this week, all jobs</span>
              <span className="text-fg tabular-nums font-medium">
                {item.projected_week_hours}h
              </span>
            </div>
            <div className="flex items-baseline justify-between mt-1">
              <span className="text-fg-muted">Over {item.threshold_hours}h by</span>
              <span className="text-fg tabular-nums">{item.hours_over}h</span>
            </div>
          </div>
        )}

        {item.code === 'double_booked' && (
          <div className="rounded-lg border border-line bg-surface-sunken p-3 text-sm space-y-1"
            data-testid="blocked-dialog-conflict-detail">
            {(item.conflicts || []).map((c, i) => (
              <p key={i} className="text-fg">
                <span className="text-fg-muted">Job </span>{c.job_number || 'another job'}
                <span className="text-fg-muted"> on </span>{c.shift_date}
                <span className="text-fg-muted"> at </span>
                <span className="tabular-nums">{c.window}</span>
              </p>
            ))}
          </div>
        )}

        {isCallOff && (
          <div className="space-y-3">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="call-off-reason-select">
                <SelectValue placeholder="Pick a reason" />
              </SelectTrigger>
              <SelectContent>
                {(reasons || []).map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {needsNote && (
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                placeholder="What happened?" data-testid="call-off-note-input" />
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="blocked-dialog-cancel">
            Leave it as it was
          </Button>
          {bulkable && (
            <Button variant="outline" onClick={() => onResolveAll({ reason, note })}
              data-testid="blocked-dialog-confirm-all">
              {meta.confirm} · all {remaining + 1}
            </Button>
          )}
          <Button disabled={!ready}
            onClick={() => (grouped ? onResolveAll({ reason, note }) : onResolve({ reason, note }))}
            data-testid="blocked-dialog-confirm">
            {grouped && otShifts > 1 ? `${meta.confirm} · ${otShifts} shifts` : meta.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
